import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Mail, LogIn } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { Glass } from '../glass/Glass';
import { supabase } from '../../lib/supabase';

const inputStyle = (hasError) => ({
  width: '100%', boxSizing: 'border-box',
  padding: '12px 16px', borderRadius: 12, fontSize: 14,
  border: hasError ? `1px solid ${T.red}` : '1px solid rgba(0,0,0,0.10)',
  background: 'rgba(255,255,255,0.85)',
  fontFamily: 'inherit', color: T.text, outline: 'none',
});

export const LoginSplash = () => {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [checkEmail, setCheckEmail] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const isSignup = mode === 'signup';

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isSignup) {
        const { data, error: err } = await supabase.auth.signUp({ email, password });
        if (err) throw err;
        if (!data.session) {
          // Supabase returns a user with no identities when the email is
          // already registered; otherwise the project wants confirmation.
          const alreadyRegistered =
            data.user && (data.user.identities || []).length === 0;
          if (alreadyRegistered) {
            setError('That email is already registered. Sign in instead.');
          } else {
            setCheckEmail(true);
          }
        }
        // With a session, AuthProvider's listener swaps the app in.
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        // AuthProvider's onAuthStateChange swaps the app in on success.
      }
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  // Google OAuth sign-in. Requires the Google provider to be enabled in the
  // Supabase dashboard (Authentication > Providers) with an OAuth client ID
  // and secret. The redirect callback is handled automatically by the Supabase
  // client (detectSessionInUrl: true), and AuthProvider swaps the app in.
  const signInWithGoogle = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({ provider: 'google' });
      if (err) throw err;
      // On success the browser redirects to Google, so nothing else to do here.
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8d8f5 0%, #d8c8ee 25%, #e0d4ee 50%, #e8d8e8 75%, #f0d8e0 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradientDrift 60s ease infinite',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, position: 'relative', overflow: 'hidden',
    }}>
      <div aria-hidden="true" style={{
        position: 'absolute', pointerEvents: 'none', zIndex: 0,
        top: '-15%', left: '-10%', width: '60vw', height: '60vw',
        background: 'radial-gradient(circle, rgba(212,146,30,0.22) 0%, rgba(212,146,30,0.08) 35%, transparent 65%)',
        filter: 'blur(40px)',
      }} />
      <div aria-hidden="true" style={{
        position: 'absolute', pointerEvents: 'none', zIndex: 0,
        bottom: '-15%', right: '-10%', width: '55vw', height: '55vw',
        background: 'radial-gradient(circle, rgba(139,114,200,0.28) 0%, rgba(139,114,200,0.10) 35%, transparent 65%)',
        filter: 'blur(40px)',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.gentle}
        style={{ width: 380, maxWidth: '100%', position: 'relative', zIndex: 1 }}
      >
        <Glass depth={3} hover={false} style={{ padding: 32 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
              background: 'linear-gradient(135deg, #f8d77a 0%, #D4921E 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(212,146,30,0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
            }}>
              {checkEmail ? <Mail size={22} color="#fff" /> : <Lock size={22} color="#fff" />}
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, letterSpacing: '-0.02em', margin: 0 }}>
              Exec HQ
            </h1>
            <p style={{ fontSize: 13, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>
              {checkEmail
                ? 'Check your inbox to confirm your email, then sign in.'
                : isSignup ? 'Create your account to continue.' : 'Sign in to continue.'}
            </p>
          </div>

          {!checkEmail && (
            <>
              <form onSubmit={submit}>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email"
                  disabled={busy}
                  autoComplete="email"
                  style={{ ...inputStyle(!!error), marginBottom: 10 }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={busy}
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  style={{ ...inputStyle(!!error), marginBottom: error ? 8 : 14 }}
                />
                {error && (
                  <div style={{ fontSize: 12, color: T.red, marginBottom: 14 }}>{error}</div>
                )}
                <button
                  type="submit"
                  disabled={!email || !password || busy}
                  style={{
                    width: '100%', padding: '12px 20px', borderRadius: 12, border: 'none',
                    background: T.amber, color: '#fff',
                    cursor: (!email || !password || busy) ? 'default' : 'pointer',
                    fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    opacity: (!email || !password || busy) ? 0.5 : 1,
                    boxShadow: '0 4px 14px rgba(212,146,30,0.30), inset 0 1px 0 rgba(255,255,255,0.30)',
                  }}
                >
                  {busy
                    ? (isSignup ? 'Creating account…' : 'Signing in…')
                    : <>{isSignup ? 'Create account' : 'Sign in'} <ArrowRight size={14} /></>}
                </button>
              </form>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0',
              }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.10)' }} />
                <span style={{ fontSize: 12, color: T.textMuted }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.10)' }} />
              </div>

              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={busy}
                style={{
                  width: '100%', padding: '12px 20px', borderRadius: 12,
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: 'rgba(255,255,255,0.85)', color: T.text,
                  cursor: busy ? 'default' : 'pointer',
                  fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: busy ? 0.5 : 1,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
                }}
              >
                <LogIn size={14} /> Continue with Google
              </button>

              <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: T.textMuted }}>
                {isSignup ? 'Already have an account?' : 'Need an account?'}{' '}
                <button
                  type="button"
                  onClick={() => { setMode(isSignup ? 'signin' : 'signup'); setError(null); }}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: T.amber, fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
                  }}
                >
                  {isSignup ? 'Sign in' : 'Sign up'}
                </button>
              </div>
            </>
          )}
        </Glass>
      </motion.div>
    </div>
  );
};
